import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, ListItem, Typography } from '@mui/material';
import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import {
    getPayModifierProductDetails,
    getPayProductOptionsDetails,
    ProductModifierPayItem
} from 'components/menu/model/Menu';
import { addOpacity } from 'lib/helpers';
import { useCurrencyString } from 'lib/useCurrencyString';

const PREFIX = 'PayOrderItem';

const classes = {
    productWrapper: `${PREFIX}productWrapper`,
    productQuantity: `${PREFIX}productQuantity`,
    productInfo: `${PREFIX}productInfo`,
    modifier: `${PREFIX}modifier`,
    quantity: `${PREFIX}quantity`,
    productPrice: `${PREFIX}productPrice`,
    modifierQuantity: `${PREFIX}modifierQuantity`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    width: '100%',
    flexDirection: 'column',
    padding: theme.spacing(0.5, 0),
    alignItems: 'flex-start',
    '& .MuiTypography-root': {
        fontSize: '14px'
    },
    [`& .${classes.productWrapper}`]: {
        display: 'flex',
        width: '100%',
        flexShrink: 0
    },

    [`& .${classes.productQuantity}`]: {
        minWidth: '24px'
    },

    [`& .${classes.productInfo}`]: {
        flex: 1
    },

    [`& .${classes.modifier}`]: {
        color: addOpacity(theme.palette.text.primary, 0.4),
        paddingLeft: theme.spacing(3)
    },

    [`& .${classes.quantity}`]: {
        color: addOpacity(theme.palette.text.primary, 0.4),
        paddingRight: 0
    },

    [`& .${classes.productPrice}`]: {
        minWidth: '64px',
        textAlign: 'right'
    },

    [`& .${classes.modifierQuantity}`]: {
        display: 'inline-block'
    }
}));

interface PayOrderItemProps {
    item: IItemReadResourceV10;
    allItems: IItemReadResourceV10[];
    onClick?: (item: IItemReadResourceV10) => void;
    secondary?: boolean;
}

export const PayOrderItem: React.FC<React.PropsWithChildren<PayOrderItemProps>> = ({
    item,
    allItems,
    onClick,
    secondary
}) => {
    const getCurrencyString = useCurrencyString();

    const [modifierProducts, calculatedItemPrice] = React.useMemo(() => {
        const products = allItems.filter(
            currentItem => currentItem.parentReferenceId && currentItem.parentReferenceId === item.referenceId
        );
        const [modifierString, modifierPrice] = getPayModifierProductDetails(products, item.quantity);
        return [modifierString, item.cost + modifierPrice];
    }, [allItems, item.quantity, item.cost, item.referenceId]);
    const renderProductModifier = React.useCallback(
        (productModifier: ProductModifierPayItem, index: number) => (
            <Typography className={classes.modifier} key={`product-modifier-${index}`}>
                <span className={classes.modifierQuantity}>
                    {productModifier.quantity > 1 ? `${productModifier.quantity}x` : ''}
                </span>
                {productModifier.title}
                {productModifier.price ? ` (${getCurrencyString(productModifier.price)})` : ''}
            </Typography>
        ),
        [getCurrencyString]
    );
    const [modifierOptions, calculatedOptionsPrice] = React.useMemo(
        () => getPayProductOptionsDetails(item.modifiers),
        [item.modifiers]
    );

    const totalPrice = React.useMemo(
        () => Math.round((calculatedItemPrice + calculatedOptionsPrice) * item.quantity * 100) / 100,
        [calculatedItemPrice, calculatedOptionsPrice, item.quantity]
    );

    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(item);
        }
    }, [item, onClick]);

    if (item.parentReferenceId) {
        return null;
    }
    return (
        <StyledListItem disableGutters onClick={handleClick} sx={{ opacity: secondary ? 0.5 : 1 }}>
            <Box className={classes.productWrapper}>
                <Typography className={classes.productQuantity}>
                    {item.quantity}
                    {'x'}
                </Typography>
                <Box className={classes.productInfo}>
                    <Typography>{item.productName}</Typography>
                </Box>
                {item.quantity > 1 && (
                    <Typography className={classes.quantity}>
                        {getCurrencyString(calculatedItemPrice)}
                    </Typography>
                )}
                <Typography className={classes.productPrice}>{getCurrencyString(totalPrice)}</Typography>
            </Box>
            {modifierProducts.map(renderProductModifier)}
            {modifierOptions.map(renderProductModifier)}
        </StyledListItem>
    );
};
